import { useEffect, useState } from "react";
import { remindersApi } from "@/lib/api";
import { PaperPlaneTilt, Clock, ChatText, BellRinging, WhatsappLogo, ArrowsClockwise } from "@phosphor-icons/react";
import { format, formatDistanceToNowStrict } from "date-fns";

const CHANNEL_META = {
  sms:      { Icon: ChatText,      label: "SMS",      cls: "bg-blue-50 text-blue-700 border-blue-200" },
  push:     { Icon: BellRinging,   label: "Push",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  whatsapp: { Icon: WhatsappLogo,  label: "WhatsApp", cls: "bg-green-50 text-green-700 border-green-200" },
  all:      { Icon: PaperPlaneTilt, label: "All",     cls: "bg-primary/10 text-primary border-primary/30" },
};

function ChannelBadge({ channel }) {
  const meta = CHANNEL_META[channel] || CHANNEL_META.all;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.cls}`}>
      <meta.Icon className="w-3 h-3" weight="fill" /> {meta.label}
    </span>
  );
}

export default function RemindersTab() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHistory = async () => {
    setRefreshing(true);
    try {
      const { data } = await remindersApi.history(200);
      setData(data);
    } catch (_) {
      setData({ rows: [], totals: {} });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="reminders-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">Reminder history</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Audit log of every SMS / Push / WhatsApp blast.</p>
        </div>
        <button
          onClick={fetchHistory}
          disabled={refreshing}
          data-testid="refresh-reminders-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-secondary text-foreground border border-border text-xs font-semibold active:scale-95 disabled:opacity-60"
        >
          <ArrowsClockwise className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} weight="bold" />
          Refresh
        </button>
      </div>

      {/* Aggregate stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="reminder-totals">
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className="text-lg font-bold">{totals.runs || 0}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Reminder blasts</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-3 text-center">
          <p className="text-lg font-bold">{(totals.recipients || 0).toLocaleString()}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Total recipients</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-blue-700">{(totals.smsSent || 0).toLocaleString()}</p>
          <p className="text-[10px] text-blue-900/70 mt-0.5">SMS sent</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-emerald-700">{(totals.pushDelivered || 0).toLocaleString()}</p>
          <p className="text-[10px] text-emerald-900/70 mt-0.5">Push delivered</p>
        </div>
      </div>

      {/* History list */}
      {data === null ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="reminders-empty">
          <PaperPlaneTilt className="w-12 h-12 text-muted-foreground mx-auto mb-3" weight="duotone" />
          <p className="font-semibold">No reminders sent yet</p>
          <p className="text-xs text-muted-foreground mt-1">Use the <strong>Outstanding</strong> KPI on the dashboard to send your first reminder.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="reminders-list">
          {rows.map((r) => (
            <div key={r.id} className="bg-card border border-border rounded-xl p-4 space-y-2" data-testid={`reminder-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm truncate">{r.deceasedName || "Bereavement"}</p>
                    <ChannelBadge channel={r.channel} />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {r.sentAt && format(new Date(r.sentAt), "dd MMM yyyy HH:mm")}
                    <span className="opacity-60">· {r.sentAt && formatDistanceToNowStrict(new Date(r.sentAt), { addSuffix: true })}</span>
                    {r.sentByName && <span className="opacity-60">· by {r.sentByName}</span>}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold leading-none">{(r.recipients || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">recipients</p>
                </div>
              </div>

              {/* Channel breakdown chips */}
              <div className="flex flex-wrap gap-1.5 text-[10px]">
                {r.sms?.sent > 0 && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">SMS sent {r.sms.sent}</span>}
                {r.sms?.failed > 0 && <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700">SMS failed {r.sms.failed}</span>}
                {r.push?.delivered > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Push {r.push.delivered}</span>}
                {r.whatsapp?.delivered > 0 && <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700">WhatsApp {r.whatsapp.delivered}</span>}
              </div>

              {/* Message preview */}
              {r.message && (
                <p className="text-xs italic text-muted-foreground line-clamp-2 border-l-2 border-border pl-3">
                  {r.message}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
